import { useTranslation } from"react-i18next";
import { useState, useEffect, useCallback } from"react";
import axios from"axios";
import Layout from"@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from"@/components/ui/card";
import { Button } from"@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from"@/components/ui/select";
import { Badge } from"@/components/ui/badge";
import { ScrollArea } from"@/components/ui/scroll-area";
import { Calendar } from"@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from"@/components/ui/popover";
import { format } from"date-fns";
import { 
 Activity, Calendar as CalendarIcon, Filter, RefreshCw,
 ShoppingCart, CreditCard, Home, FileText, Users, Briefcase,
 Building, CheckCircle, Clock, TrendingUp, ChevronRight, ChevronLeft
} from"lucide-react";
import { toast } from"sonner";

// Activity type icons
const ACTIVITY_ICONS = {
 reservation_created: ShoppingCart,
 reservation_updated: ShoppingCart,
 reservation_cancelled: ShoppingCart,
 sale_confirmed: CheckCircle,
 payment_received: CreditCard,
 payment_scheduled: Clock,
 invoice_created: FileText,
 invoice_paid: CreditCard,
 invoice_partially_paid: CreditCard,
 offer_accepted: Briefcase,
 offer_rejected: Briefcase,
 rental_created: Home,
 project_created: Building,
 task_created: Activity,
 task_completed: CheckCircle,
 budget_updated: TrendingUp,
 cost_recorded: CreditCard,
};

// Activity type colors
const ACTIVITY_COLORS = {
 reservation_created:"bg-blue-500/10 text-blue-700 border-blue-300",
 sale_confirmed:"bg-emerald-500/10 text-emerald-700 border-green-300",
 payment_received:"bg-emerald-500/10 text-emerald-700 border-green-300",
 invoice_created:"bg-orange-100 text-orange-700 border-orange-300",
 invoice_paid:"bg-emerald-500/10 text-emerald-700 border-green-300",
 invoice_partially_paid:"bg-amber-500/10 text-amber-700 border-yellow-200/60",
 offer_accepted:"bg-purple-100 text-purple-700 border-purple-300",
 offer_rejected:"bg-rose-500/10 text-rose-700 border-red-300",
 rental_created:"bg-indigo-100 text-indigo-700 border-indigo-300",
 task_completed:"bg-emerald-500/10 text-emerald-700 border-green-300",
 default:"bg-white/60 text-[#334155] border-slate-200",
};

const CATEGORIES = [
 { value:"all", label:"All Activities"},
 { value:"sales", label:"Sales & Reservations"},
 { value:"payments", label:"Payments"},
 { value:"invoices", label:"Invoices"},
 { value:"rentals", label:"Rentals"},
 { value:"projects", label:"Projects & Units"},
 { value:"tasks", label:"Tasks"},
 { value:"finance", label:"Finance"},
];

const ActivityLogsPage = ({ user, onLogout }) => {
 const { t } = useTranslation();
 const [logs, setLogs] = useState([]);
 const [stats, setStats] = useState(null);
 const [projects, setProjects] = useState([]);
 const [loading, setLoading] = useState(true);
 const [total, setTotal] = useState(0);
 
 // Filters
 const [selectedProject, setSelectedProject] = useState("all");
 const [selectedCategory, setSelectedCategory] = useState("all");
 const [startDate, setStartDate] = useState(null);
 const [endDate, setEndDate] = useState(null);
 
 // Pagination
 const [page, setPage] = useState(0);
 const [limit] = useState(50);

 const loadLogs = useCallback(async () => {
 setLoading(true);
 try {
 let url = `/activity-logs?limit=${limit}&skip=${page * limit}`;
 
 if (selectedProject !=="all") {
 url += `&project_id=${selectedProject}`;
 }
 if (selectedCategory !=="all") {
 url += `&category=${selectedCategory}`;
 }
 if (startDate) {
 url += `&start_date=${startDate.toISOString()}`;
 }
 if (endDate) {
 url += `&end_date=${endDate.toISOString()}`;
 }
 
 const response = await axios.get(url);
 setLogs(response.data.logs);
 setTotal(response.data.total);
 } catch (error) {
 console.error("Failed to load activity logs:", error);
 if (error.response?.status !== 403) {
 toast.error(t("messages.loadFailed"));
 }
 } finally {
 setLoading(false);
 }
 }, [selectedProject, selectedCategory, startDate, endDate, page, limit]);

 const loadStats = async () => {
 try {
 let url ="/activity-logs/stats?days=30";
 if (selectedProject !=="all") {
 url += `&project_id=${selectedProject}`;
 }
 const response = await axios.get(url);
 setStats(response.data);
 } catch (error) {
 console.error("Failed to load stats");
 }
 };

 const loadProjects = async () => {
 try {
 const response = await axios.get("/projects");
 setProjects(response.data);
 } catch (error) {
 console.error("Failed to load projects");
 }
 };

 useEffect(() => {
 loadProjects();
 }, []);

 useEffect(() => {
 loadLogs();
 loadStats();
 }, [loadLogs]);

 const formatTimestamp = (timestamp) => {
 if (!timestamp) return"";
 const date = new Date(timestamp);
 const now = new Date();
 const diffMs = now - date;
 const diffMins = Math.floor(diffMs / 60000);
 const diffHours = Math.floor(diffMs / 3600000);
 const diffDays = Math.floor(diffMs / 86400000);

 if (diffMins < 1) return"Just now";
 if (diffMins < 60) return `${diffMins}m ago`;
 if (diffHours < 24) return `${diffHours}h ago`;
 if (diffDays < 7) return `${diffDays}d ago`;
 
 return format(date,"MMM d, yyyy HH:mm");
 };

 const getActivityIcon = (type) => {
 const Icon = ACTIVITY_ICONS[type] || Activity;
 return Icon;
 };

 const getActivityColor = (type) => {
 return ACTIVITY_COLORS[type] || ACTIVITY_COLORS.default;
 };

 const formatActivityType = (type) => {
 return type.split("_").map(w => w.charAt(0).toUpperCase() + w.slice(1)).join("");
 };

 const totalPages = Math.ceil(total / limit);

 // Check if user is owner
 if (user?.role !=="owner") {
 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="flex items-center justify-center h-full">
 <Card className="p-8 text-center">
 <Activity className="w-12 h-12 text-slate-300 mx-auto mb-4"/>
 <h2 className="text-xl font-semibold text-[#334155]">Access Restricted</h2>
 <p className="text-slate-500 mt-2">Activity logs are only available to owners.</p>
 </Card>
 </div>
 </Layout>
 );
 }

 return (
 <Layout user={user} onLogout={onLogout}>
 <div className="h-full flex flex-col">
 {/* Header */}
 <div className="p-6 border-b bg-white">
 <div className="flex items-center justify-between mb-4">
 <div>
 <h1 className="font-display text-3xl font-bold tracking-tight uppercase text-slate-900">
 Progress Log
 </h1>
 <p className="text-slate-500 mt-1">
 Automatic timeline of all system activities
 </p>
 </div>
 <Button 
 variant="outline"
 onClick={() => { loadLogs(); loadStats(); }}
 className="gap-2"
 >
 <RefreshCw className="w-4 h-4"/>
 Refresh
 </Button>
 </div>

 {/* Stats */}
 {stats && (
 <div className="grid grid-cols-6 gap-3 mb-4">
 <Card className="p-3">
 <p className="text-2xl font-bold text-slate-800">{stats.total}</p>
 <p className="text-[11px] text-slate-500">Last 30 Days</p>
 </Card>
 <Card className="p-3 bg-green-50/80">
 <p className="text-2xl font-bold text-slate-900">{stats.highlights?.sales_activities || 0}</p>
 <p className="text-xs text-green-600">Sales Activities</p>
 </Card>
 <Card className="p-3 bg-blue-50/80">
 <p className="text-2xl font-bold text-slate-900">{stats.highlights?.payment_activities || 0}</p>
 <p className="text-xs text-blue-600">Payment Activities</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-[#334155]">{stats.by_category?.invoices || 0}</p>
 <p className="text-[11px] text-slate-500">Invoice Activities</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-[#334155]">{stats.by_category?.tasks || 0}</p>
 <p className="text-[11px] text-slate-500">Task Activities</p>
 </Card>
 <Card className="p-3">
 <p className="text-2xl font-bold text-[#334155]">{stats.by_category?.projects || 0}</p>
 <p className="text-[11px] text-slate-500">Project Activities</p>
 </Card>
 </div>
 )}

 {/* Filters */}
 <div className="flex items-center gap-3 flex-wrap">
 <div className="flex items-center gap-2">
 <Filter className="w-4 h-4 text-slate-400"/>
 <span className="text-sm text-slate-500">Filters:</span>
 </div>
 
 <Select value={selectedProject} onValueChange={(v) => { setSelectedProject(v); setPage(0); }}>
 <SelectTrigger className="w-48">
 <SelectValue placeholder="Project"/>
 </SelectTrigger>
 <SelectContent>
 <SelectItem value="all">All Projects</SelectItem>
 {projects.map(p => (
 <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
 ))}
 </SelectContent>
 </Select>
 
 <Select value={selectedCategory} onValueChange={(v) => { setSelectedCategory(v); setPage(0); }}>
 <SelectTrigger className="w-48">
 <SelectValue placeholder="Category"/>
 </SelectTrigger>
 <SelectContent>
 {CATEGORIES.map(cat => (
 <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
 ))}
 </SelectContent>
 </Select>

 <Popover>
 <PopoverTrigger asChild>
 <Button variant="outline"className="gap-2">
 <CalendarIcon className="w-4 h-4"/>
 {startDate ? format(startDate,"MMM d") :"Start Date"}
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-0">
 <Calendar
 mode="single"
 selected={startDate}
 onSelect={(d) => { setStartDate(d); setPage(0); }}
 />
 </PopoverContent>
 </Popover>

 <Popover>
 <PopoverTrigger asChild>
 <Button variant="outline"className="gap-2">
 <CalendarIcon className="w-4 h-4"/>
 {endDate ? format(endDate,"MMM d") :"End Date"}
 </Button>
 </PopoverTrigger>
 <PopoverContent className="w-auto p-0">
 <Calendar
 mode="single"
 selected={endDate}
 onSelect={(d) => { setEndDate(d); setPage(0); }}
 />
 </PopoverContent>
 </Popover>

 {(startDate || endDate || selectedCategory !=="all"|| selectedProject !=="all") && (
 <Button 
 variant="ghost"
 size="sm"
 onClick={() => {
 setStartDate(null);
 setEndDate(null);
 setSelectedCategory("all");
 setSelectedProject("all");
 setPage(0);
 }}
 >
 Clear Filters
 </Button>
 )}
 </div>
 </div>

 {/* Activity Timeline */}
 <ScrollArea className="flex-1 p-6">
 {loading ? (
 <div className="flex items-center justify-center h-64">
 <p className="text-slate-500">Loading activities...</p>
 </div>
 ) : logs.length > 0 ? (
 <div className="relative">
 {/* Timeline line */}
 <div className="absolute left-6 top-0 bottom-0 w-0.5 bg-slate-200"/>
 
 <div className="space-y-4">
 {logs.map((log, idx) => {
 const Icon = getActivityIcon(log.type);
 const colorClass = getActivityColor(log.type);
 
 return (
 <div key={log.id || idx} className="relative flex gap-4 ml-3">
 {/* Timeline dot */}
 <div className={`relative z-10 flex items-center justify-center w-7 h-7 rounded-full border-2 ${colorClass}`}>
 <Icon className="w-3.5 h-3.5"/>
 </div>
 
 {/* Content */}
 <Card className="flex-1 hover:shadow-md transition-shadow">
 <CardContent className="p-4">
 <div className="flex items-start justify-between">
 <div className="flex-1">
 <div className="flex items-center gap-2 mb-1">
 <h4 className="font-semibold text-slate-800">{log.title}</h4>
 <Badge variant="outline"className={`text-xs ${colorClass}`}>
 {formatActivityType(log.type)}
 </Badge>
 </div>
 <p className="text-sm text-slate-500">{log.description}</p>
 
 <div className="flex items-center gap-4 mt-2 text-xs text-slate-400">
 {log.project_name && (
 <span className="flex items-center gap-1">
 <Building className="w-3 h-3"/>
 {log.project_name}
 </span>
 )}
 {log.unit_code && (
 <span className="flex items-center gap-1">
 <Home className="w-3 h-3"/>
 Unit {log.unit_code}
 </span>
 )}
 {log.user_name && (
 <span className="flex items-center gap-1">
 <Users className="w-3 h-3"/>
 {log.user_name}
 </span>
 )}
 </div>
 </div>
 
 <div className="text-right">
 <p className="text-sm font-medium text-slate-500">
 {formatTimestamp(log.timestamp)}
 </p>
 <p className="text-xs text-slate-400">
 {format(new Date(log.timestamp),"HH:mm")}
 </p>
 </div>
 </div>
 
 {/* Metadata preview */}
 {log.metadata && Object.keys(log.metadata).length > 0 && (
 <div className="mt-3 pt-3 border-t border-white/40">
 <div className="flex flex-wrap gap-2">
 {log.metadata.contract_value && (
 <Badge variant="secondary"className="text-xs">
 Contract: €{log.metadata.contract_value.toLocaleString()}
 </Badge>
 )}
 {log.metadata.amount && (
 <Badge variant="secondary"className="text-xs">
 Amount: €{log.metadata.amount.toLocaleString()}
 </Badge>
 )}
 {log.metadata.payment_amount && (
 <Badge variant="secondary"className="text-xs">
 Payment: €{log.metadata.payment_amount.toLocaleString()}
 </Badge>
 )}
 {log.metadata.buyer_name && (
 <Badge variant="secondary"className="text-xs">
 Buyer: {log.metadata.buyer_name}
 </Badge>
 )}
 {log.metadata.company_name && (
 <Badge variant="secondary"className="text-xs">
 Company: {log.metadata.company_name}
 </Badge>
 )}
 </div>
 </div>
 )}
 </CardContent>
 </Card>
 </div>
 );
 })}
 </div>
 </div>
 ) : (
 <div className="flex flex-col items-center justify-center h-64 text-center">
 <Activity className="w-12 h-12 text-slate-300 mb-4"/>
 <h3 className="text-lg font-medium text-slate-600">No activities found</h3>
 <p className="text-slate-500 mt-1">
 Activities will appear here as they occur
 </p>
 </div>
 )}
 </ScrollArea>

 {/* Pagination */}
 {total > limit && (
 <div className="p-4 border-t bg-white flex items-center justify-between">
 <p className="text-sm text-slate-500">
 Showing {page * limit + 1} - {Math.min((page + 1) * limit, total)} of {total}
 </p>
 <div className="flex items-center gap-2">
 <Button
 variant="outline"
 size="sm"
 onClick={() => setPage(Math.max(0, page - 1))}
 disabled={page === 0}
 >
 <ChevronLeft className="w-4 h-4"/>
 Previous
 </Button>
 <span className="text-sm text-slate-500">
 Page {page + 1} of {totalPages}
 </span>
 <Button
 variant="outline"
 size="sm"
 onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
 disabled={page >= totalPages - 1}
 >
 Next
 <ChevronRight className="w-4 h-4"/>
 </Button>
 </div>
 </div>
 )}
 </div>
 </Layout>
 );
};

export default ActivityLogsPage;
